// Performance monitoring utility for tracking CPU optimizations

interface Measurement {
  label: string;
  startTime: number;
  startMemory: number;
}

interface CpuTimeMetric {
  label: string;
  time: number;
  timestamp: number;
}

interface MemoryMetric {
  label: string;
  delta?: number;
  value?: number;
  timestamp: number;
}

interface AudioLatencyMetric {
  value: number;
  timestamp: number;
}

interface PerformanceMetricsData {
  cpuTime: CpuTimeMetric[];
  memoryUsage: MemoryMetric[];
  gcCount: number;
  audioLatency: AudioLatencyMetric[];
  frameDrops: number;
  startTime: number | null;
}

interface MemoryStats {
  min: string;
  max: string;
  avg: string;
}

interface PerformanceReport {
  duration: string;
  avgCpuTime: string;
  avgAudioLatency: string;
  gcCount: number;
  frameDrops: number;
  memoryStats: MemoryStats;
}

export class PerformanceMonitor {
  metrics: PerformanceMetricsData;
  isMonitoring: boolean;
  private observers: unknown[];
  private memoryInterval: number | null = null;

  constructor() {
    this.metrics = {
      cpuTime: [],
      memoryUsage: [],
      gcCount: 0,
      audioLatency: [],
      frameDrops: 0,
      startTime: null
    };

    this.isMonitoring = false;
    this.observers = [];
  }

  startMeasurement(label: string): Measurement | undefined {
    if (!this.isMonitoring) {
      return undefined;
    }

    const measurement: Measurement = {
      label,
      startTime: performance.now(),
      startMemory:
        (performance as typeof performance & { memory?: { usedJSHeapSize: number } }).memory
          ?.usedJSHeapSize || 0
    };

    return measurement;
  }

  endMeasurement(measurement: Measurement | undefined): void {
    if (!this.isMonitoring || !measurement) {
      return;
    }

    const elapsed = performance.now() - measurement.startTime;
    const memoryDelta =
      ((performance as typeof performance & { memory?: { usedJSHeapSize: number } }).memory
        ?.usedJSHeapSize || 0) - measurement.startMemory;

    this.metrics.cpuTime.push({
      label: measurement.label,
      time: elapsed,
      timestamp: Date.now()
    });

    if (memoryDelta < 0) {
      // Negative delta suggests GC occurred
      this.metrics.gcCount++;
    }

    this.metrics.memoryUsage.push({
      label: measurement.label,
      delta: memoryDelta,
      timestamp: Date.now()
    });
  }

  measureAudioLatency(audioContext: AudioContext): void {
    if (!audioContext) {
      return;
    }

    const latency = (audioContext.baseLatency || 0) + (audioContext.outputLatency || 0);
    this.metrics.audioLatency.push({
      value: latency * 1000, // Convert to milliseconds
      timestamp: Date.now()
    });
  }

  trackFrameDrop(): void {
    this.metrics.frameDrops++;
  }

  startMonitoring(): void {
    this.isMonitoring = true;
    this.metrics.startTime = Date.now();

    // Monitor frame drops
    let lastFrameTime = performance.now();
    const checkFrameDrops = (): void => {
      if (!this.isMonitoring) {
        return;
      }

      const currentTime = performance.now();
      const frameDelta = currentTime - lastFrameTime;

      // If frame took longer than 33ms (30fps), consider it dropped
      if (frameDelta > 33) {
        this.trackFrameDrop();
      }

      lastFrameTime = currentTime;
      requestAnimationFrame(checkFrameDrops);
    };
    requestAnimationFrame(checkFrameDrops);

    // Periodic memory sampling
    this.memoryInterval = setInterval(() => {
      const perfWithMemory = performance as typeof performance & {
        memory?: { usedJSHeapSize: number };
      };
      if (perfWithMemory.memory) {
        this.metrics.memoryUsage.push({
          label: 'periodic',
          value: perfWithMemory.memory.usedJSHeapSize,
          timestamp: Date.now()
        });
      }
    }, 1000) as unknown as number;
  }

  stopMonitoring(): void {
    this.isMonitoring = false;

    if (this.memoryInterval) {
      clearInterval(this.memoryInterval);
      this.memoryInterval = null;
    }
  }

  getReport(): PerformanceReport {
    const duration = this.metrics.startTime ? (Date.now() - this.metrics.startTime) / 1000 : 0;

    const avgCpuTime =
      this.metrics.cpuTime.length > 0
        ? this.metrics.cpuTime.reduce((sum, m) => sum + m.time, 0) / this.metrics.cpuTime.length
        : 0;

    const avgLatency =
      this.metrics.audioLatency.length > 0
        ? this.metrics.audioLatency.reduce((sum, m) => sum + m.value, 0) /
          this.metrics.audioLatency.length
        : 0;

    const memoryStats = this.calculateMemoryStats();

    return {
      duration: `${duration.toFixed(1)}s`,
      avgCpuTime: `${avgCpuTime.toFixed(2)}ms`,
      avgAudioLatency: `${avgLatency.toFixed(2)}ms`,
      gcCount: this.metrics.gcCount,
      frameDrops: this.metrics.frameDrops,
      memoryStats
    };
  }

  calculateMemoryStats(): MemoryStats {
    if (this.metrics.memoryUsage.length === 0) {
      return { min: '0MB', max: '0MB', avg: '0MB' };
    }

    const periodicSamples = this.metrics.memoryUsage
      .filter(m => m.label === 'periodic')
      .map(m => m.value || 0);

    if (periodicSamples.length === 0) {
      return { min: '0MB', max: '0MB', avg: '0MB' };
    }

    const min = Math.min(...periodicSamples) / 1048576; // Convert to MB
    const max = Math.max(...periodicSamples) / 1048576;
    const avg = periodicSamples.reduce((a, b) => a + b, 0) / periodicSamples.length / 1048576;

    return {
      min: `${min.toFixed(2)}MB`,
      max: `${max.toFixed(2)}MB`,
      avg: `${avg.toFixed(2)}MB`
    };
  }

  logReport(): PerformanceReport {
    const report = this.getReport();
    console.log('=== Performance Report ===');
    console.log(`Duration: ${report.duration}`);
    console.log(`Average CPU Time: ${report.avgCpuTime}`);
    console.log(`Average Audio Latency: ${report.avgAudioLatency}`);
    console.log(`Garbage Collections: ${report.gcCount}`);
    console.log(`Frame Drops: ${report.frameDrops}`);
    console.log(`Memory Usage:`, report.memoryStats);
    console.log('========================');
    return report;
  }
}

// Export singleton instance for easy access
export const performanceMonitor = new PerformanceMonitor();
